import React, { useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { Check, Copy, Plus, Trash2, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { generateCoupons } from "../../lib/coupons";

const tabs = ["all", "pending", "active", "used", "declined", "expired"];
const bulkCouponOptions = [10, 20, 50, 100, 200, 300];
const topSelectOptions = [10, 20, 50, 100, 200, 300];
const initialForm = {
  code: "",
  assignedToName: "",
  assignedToEmail: "",
  status: "pending",
  expiresAt: "",
};
const COUPON_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const COUPON_CODE_PATTERN = /^[A-Z0-9]{10}$/;

const normalizeCouponCode = (value = "") =>
  String(value).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);

const createCode = (existing) => {
  let code = "";
  do {
    code = Array.from({ length: 10 }, () =>
      COUPON_CODE_ALPHABET[Math.floor(Math.random() * COUPON_CODE_ALPHABET.length)]
    ).join("");
  } while (existing.has(code));
  existing.add(code);
  return code;
};

const toDateTime = (value) => {
  if (!value) return "-";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const AdminCoupons = () => {
  const { profile } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [topSelectCount, setTopSelectCount] = useState(100);
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "coupons"));
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => String(b.createdAt?.seconds || "").localeCompare(String(a.createdAt?.seconds || "")));
      setCoupons(rows);
      setSelectedIds((current) => current.filter((id) => rows.some((coupon) => coupon.id === id)));
    } catch (error) {
      toast.error(error.message || "Unable to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const next = { all: coupons.length, pending: 0, active: 0, used: 0, declined: 0, expired: 0 };
    coupons.forEach((coupon) => {
      if (next[coupon.status] !== undefined) next[coupon.status] += 1;
    });
    return next;
  }, [coupons]);

  const visibleCoupons = activeTab === "all"
    ? coupons
    : coupons.filter((coupon) => coupon.status === activeTab);
  const selectedVisibleIds = visibleCoupons
    .map((coupon) => coupon.id)
    .filter((id) => selectedIds.includes(id));
  const allVisibleSelected = visibleCoupons.length > 0 && selectedVisibleIds.length === visibleCoupons.length;

  const saveManualCoupon = async (event) => {
    event.preventDefault();
    const existing = new Set(coupons.map((coupon) => coupon.id));
    const code = normalizeCouponCode(form.code) || createCode(existing);
    if (!COUPON_CODE_PATTERN.test(code)) {
      toast.error("Coupon code must be 10 letters or numbers");
      return;
    }
    if (coupons.some((coupon) => coupon.id === code)) {
      toast.error("Coupon code already exists");
      return;
    }
    if (!form.assignedToEmail.trim()) {
      toast.error("Assign the coupon to an email");
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, "coupons", code), {
        code,
        discountType: "percent",
        discountValue: 100,
        assignedToName: form.assignedToName.trim(),
        assignedToEmail: form.assignedToEmail.trim().toLowerCase(),
        assignedToUserId: null,
        eventId: "",
        status: form.status,
        isOneTimeUse: true,
        createdBy: profile?.email || "admin",
        createdAt: serverTimestamp(),
        approvedByAdmin: form.status === "active",
        approvedBy: form.status === "active" ? profile?.email || "admin" : null,
        approvedAt: form.status === "active" ? serverTimestamp() : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
        used: false,
        usedByUserId: null,
        usedByName: null,
        usedByEmail: null,
        usedAt: null,
        registrationId: null,
      });
      toast.success("Coupon created");
      setForm(initialForm);
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to create coupon");
    } finally {
      setSaving(false);
    }
  };

  const generateBulkCoupons = async (count) => {
    setSaving(true);
    try {
      let generated = 0;
      try {
        const result = await generateCoupons(count);
        generated = result.generated || count;
      } catch (backendError) {
        console.warn("Backend coupon generation unavailable, using admin Firestore fallback:", backendError);
        const existing = new Set(coupons.map((coupon) => coupon.id));
        const createdBy = profile?.email || "admin";
        const writes = Array.from({ length: count }, () => {
          const code = createCode(existing);
          return setDoc(doc(db, "coupons", code), {
            code,
            discountType: "percent",
            discountValue: 100,
            assignedToName: "",
            assignedToEmail: "",
            assignedToUserId: null,
            eventId: "",
            status: "pending",
            isOneTimeUse: true,
            createdBy,
            createdAt: serverTimestamp(),
            approvedByAdmin: false,
            approvedBy: null,
            approvedAt: null,
            expiresAt: null,
            used: false,
            usedByUserId: null,
            usedByName: null,
            usedByEmail: null,
            usedAt: null,
            registrationId: null,
          });
        });
        await Promise.all(writes);
        generated = writes.length;
      }
      toast.success(`Generated ${generated} pending coupons`);
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to generate coupons");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (coupon, status) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "coupons", coupon.id), {
        status,
        approvedByAdmin: status === "active",
        approvedBy: status === "active" ? profile?.email || "admin" : null,
        approvedAt: status === "active" ? serverTimestamp() : null,
        declinedBy: status === "declined" ? profile?.email || "admin" : null,
        declinedAt: status === "declined" ? serverTimestamp() : null,
      });
      toast.success(status === "active" ? "Coupon accepted" : "Coupon declined");
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to update coupon");
    } finally {
      setSaving(false);
    }
  };

  const updateCouponsStatus = async (ids, status) => {
    if (ids.length === 0) {
      toast.error("Select at least one coupon");
      return;
    }

    const selectedCoupons = coupons.filter((coupon) => ids.includes(coupon.id));
    const eligibleCoupons = selectedCoupons.filter((coupon) => coupon.status !== "used");
    if (eligibleCoupons.length === 0) {
      toast.error("Used coupons cannot be changed");
      return;
    }

    setSaving(true);
    try {
      for (let i = 0; i < eligibleCoupons.length; i += 450) {
        const batch = writeBatch(db);
        eligibleCoupons.slice(i, i + 450).forEach((coupon) => {
          batch.update(doc(db, "coupons", coupon.id), {
            status,
            approvedByAdmin: status === "active",
            approvedBy: status === "active" ? profile?.email || "admin" : null,
            approvedAt: status === "active" ? serverTimestamp() : null,
            declinedBy: status === "declined" ? profile?.email || "admin" : null,
            declinedAt: status === "declined" ? serverTimestamp() : null,
          });
        });
        await batch.commit();
      }

      const skipped = selectedCoupons.length - eligibleCoupons.length;
      toast.success(
        `${status === "active" ? "Accepted" : "Declined"} ${eligibleCoupons.length} coupon${eligibleCoupons.length === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} used` : ""}`
      );
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to update coupons");
    } finally {
      setSaving(false);
    }
  };

  const toggleCoupon = (id) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleVisibleCoupons = () => {
    const visibleIds = visibleCoupons.map((coupon) => coupon.id);
    setSelectedIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleIds.includes(id));
      }
      return Array.from(new Set([...current, ...visibleIds]));
    });
  };

  const selectTopCoupons = () => {
    const topIds = visibleCoupons.slice(0, topSelectCount).map((coupon) => coupon.id);
    setSelectedIds((current) => Array.from(new Set([...current, ...topIds])));
  };

  const copyCouponList = async (couponList, label) => {
    if (couponList.length === 0) {
      toast.error("No coupons to copy");
      return;
    }

    const text = couponList
      .map((coupon, index) => `${index + 1}. ${coupon.code || coupon.id}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${couponList.length} ${label}`);
    } catch (error) {
      toast.error("Unable to copy coupons");
    }
  };

  const copyTopCoupons = () => {
    copyCouponList(visibleCoupons.slice(0, topSelectCount), `top coupon${topSelectCount === 1 ? "" : "s"}`);
  };

  const copySelectedCoupons = () => {
    const selectedCoupons = coupons.filter((coupon) => selectedIds.includes(coupon.id));
    copyCouponList(selectedCoupons, `selected coupon${selectedCoupons.length === 1 ? "" : "s"}`);
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const deleteCoupons = async (ids, label) => {
    if (ids.length === 0) {
      toast.error("Select at least one coupon");
      return;
    }
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;

    setSaving(true);
    try {
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach((id) => batch.delete(doc(db, "coupons", id)));
        await batch.commit();
      }
      setSelectedIds((current) => current.filter((id) => !ids.includes(id)));
      toast.success(`Deleted ${ids.length} coupon${ids.length === 1 ? "" : "s"}`);
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to delete coupons");
    } finally {
      setSaving(false);
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code || coupon.id}? This cannot be undone.`)) return;

    setSaving(true);
    try {
      await deleteDoc(doc(db, "coupons", coupon.id));
      setSelectedIds((current) => current.filter((id) => id !== coupon.id));
      toast.success("Coupon deleted");
      await load();
    } catch (error) {
      toast.error(error.message || "Unable to delete coupon");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-coupons">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Coupons</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Coupon Management</h1>
          <p className="mt-1 text-slate-600">Create assigned 100% coupons and track one-time usage.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {bulkCouponOptions.map((count) => (
            <button
              key={count}
              type="button"
              onClick={() => generateBulkCoupons(count)}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <TicketPercent className="h-4 w-4" /> Generate {count}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={saveManualCoupon} className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-blue-600" />
          <h2 className="font-heading text-xl font-bold text-slate-900">Add Manual Coupon</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Coupon Code" value={form.code} onChange={(value) => setForm((prev) => ({ ...prev, code: normalizeCouponCode(value) }))} placeholder="Auto-generate" />
          <Field label="Assigned Name" value={form.assignedToName} onChange={(value) => setForm((prev) => ({ ...prev, assignedToName: value }))} placeholder="Optional" />
          <Field label="Assigned Email" type="email" value={form.assignedToEmail} onChange={(value) => setForm((prev) => ({ ...prev, assignedToEmail: value }))} />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Status</label>
            <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="pending">Pending</option>
              <option value="active">Active</option>
            </select>
          </div>
          <Field label="Expiry Date" type="date" value={form.expiresAt} onChange={(value) => setForm((prev) => ({ ...prev, expiresAt: value }))} />
        </div>
        <div className="mt-4 flex justify-end">
          <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            Create Coupon
          </button>
        </div>
      </form>

      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`rounded-lg px-3 py-2 text-sm font-semibold capitalize ${activeTab === tab ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            {tab} ({counts[tab] || 0})
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-700">{selectedIds.length} selected</div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-700">Select top</span>
            <select
              value={topSelectCount}
              onChange={(event) => setTopSelectCount(Number(event.target.value))}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
              aria-label="Select top coupon count"
            >
              {topSelectOptions.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={selectTopCoupons}
              disabled={saving || visibleCoupons.length === 0}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={copyTopCoupons}
              disabled={saving || visibleCoupons.length === 0}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Copy className="h-4 w-4" /> Copy Top
            </button>
          </div>
          <button
            type="button"
            onClick={toggleVisibleCoupons}
            disabled={saving || visibleCoupons.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            {allVisibleSelected ? "Unselect Visible" : "Select All Visible"}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={saving || selectedIds.length === 0}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={copySelectedCoupons}
            disabled={saving || selectedIds.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Copy className="h-4 w-4" /> Copy Selected
          </button>
          <button
            type="button"
            onClick={() => updateCouponsStatus(selectedIds, "active")}
            disabled={saving || selectedIds.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check className="h-4 w-4" /> Accept Selected
          </button>
          <button
            type="button"
            onClick={() => updateCouponsStatus(selectedIds, "declined")}
            disabled={saving || selectedIds.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            <X className="h-4 w-4" /> Decline Selected
          </button>
          <button
            type="button"
            onClick={() => deleteCoupons(selectedIds, `${selectedIds.length} selected coupon${selectedIds.length === 1 ? "" : "s"}`)}
            disabled={saving || selectedIds.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Delete Selected
          </button>
          <button
            type="button"
            onClick={() => deleteCoupons(coupons.map((coupon) => coupon.id), `all ${coupons.length} coupons`)}
            disabled={saving || coupons.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Delete All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : visibleCoupons.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No coupons found.</div>
        ) : (
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="w-12 px-5 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleCoupons}
                    aria-label="Select visible coupons"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                  />
                </th>
                <th className="px-5 py-3 font-semibold">Coupon Code</th>
                <th className="px-5 py-3 font-semibold">Assigned To</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Used By</th>
                <th className="px-5 py-3 font-semibold">Used At</th>
                <th className="px-5 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleCoupons.map((coupon) => (
                <tr key={coupon.id} className="border-t border-slate-100 align-top">
                  <td className="px-5 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(coupon.id)}
                      onChange={() => toggleCoupon(coupon.id)}
                      aria-label={`Select coupon ${coupon.code || coupon.id}`}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                    />
                  </td>
                  <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-900">{coupon.code || coupon.id}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{coupon.assignedToName || "-"}</div>
                    <div className="text-xs text-slate-500">{coupon.assignedToEmail || "Any email"}</div>
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={coupon.status} /></td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{coupon.usedByName || "-"}</div>
                    <div className="text-xs text-slate-500">{coupon.usedByEmail || "-"}</div>
                    {coupon.registrationId && <div className="mt-1 font-mono text-xs text-slate-400">{coupon.registrationId}</div>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{toDateTime(coupon.usedAt)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      {coupon.status === "pending" && (
                        <button type="button" onClick={() => updateStatus(coupon, "active")} disabled={saving} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                          <Check className="h-4 w-4" /> Accept
                        </button>
                      )}
                      {["pending", "active"].includes(coupon.status) && (
                        <button type="button" onClick={() => updateStatus(coupon, "declined")} disabled={saving} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50">
                          <X className="h-4 w-4" /> Decline
                        </button>
                      )}
                      {coupon.status === "used" && <span className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">View details</span>}
                      <button type="button" onClick={() => deleteCoupon(coupon)} disabled={saving} className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-red-700 ring-1 ring-red-100 hover:bg-red-50 disabled:opacity-50">
                        <Trash2 className="h-4 w-4" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
    />
  </div>
);

const StatusBadge = ({ status = "pending" }) => {
  const styles = {
    pending: "bg-amber-50 text-amber-700",
    active: "bg-emerald-50 text-emerald-700",
    used: "bg-slate-100 text-slate-700",
    declined: "bg-red-50 text-red-700",
    expired: "bg-zinc-100 text-zinc-700",
  };
  return (
    <span className={`rounded-full px-2 py-1 text-xs font-semibold capitalize ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

export default AdminCoupons;
